import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Loader2,
  ChevronRight,
  Users,
  KeyRound,
  ShieldOff,
  ShieldCheck,
  Unlink,
  Edit2,
  Trash2,
  Check,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { AuthContext } from '../../contexts/AuthContext';
import caregiverService from '../../services/caregiverService';
import CaregiverStatusBadge, { STATUS_VALUES } from './statusBadge';
import ResetPinModal from './ResetPinModal';
import DeactivateLinkConfirm from './DeactivateLinkConfirm';
import EmptyState from '../ui/EmptyState';

const FILTER_OPTIONS = ['All', ...STATUS_VALUES];

// Compact icon button for the row actions; each action adds its own colour.
const ICON_BTN = 'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Study-level caregivers admin tab. Lists every caregiver linked to any
 * participant in the study, with link counts + worst-status, plus the link
 * history per row. Click a row → walk to the participant's Caregivers tab
 * with that caregiver pre-selected.
 */
const StudyCaregiversTab = ({ studyID }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isSuperuser = !!user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [allowCaregivers, setAllowCaregivers] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [pinResetTarget, setPinResetTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // `${caregiver_id}:lock` | ...

  const load = async () => {
    setLoading(true);
    try {
      const data = await caregiverService.listForStudy(studyID);
      setAllowCaregivers(!!data.allow_caregivers);
      setRows(data.caregivers || []);
    } catch (err) {
      console.error('failed to load study caregivers', err);
      toast.error('Failed to load caregivers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studyID) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyID]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'All' && r.status !== statusFilter) return false;
      if (!q) return true;
      const cg = r.caregiver || {};
      return (
        (cg.full_name && cg.full_name.toLowerCase().includes(q)) ||
        (cg.first_name && cg.first_name.toLowerCase().includes(q)) ||
        (cg.last_name && cg.last_name.toLowerCase().includes(q)) ||
        (cg.email && cg.email.toLowerCase().includes(q)) ||
        (r.caregiver_id && r.caregiver_id.toLowerCase().includes(q))
      );
    });
  }, [rows, search, statusFilter]);

  const handleLock = async (row) => {
    const cgId = row.caregiver?.id;
    if (!cgId) return;
    const name = row.caregiver?.full_name || row.caregiver_id;
    if (!confirm(`Lock ${name}'s account? They'll need to contact you for a PIN reset to regain access.`)) {
      return;
    }
    setBusyAction(`${cgId}:lock`);
    try {
      await caregiverService.lock(cgId);
      toast.success('Account locked.');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(detail || 'Failed to lock account.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleHardDelete = async (row) => {
    const cgId = row.caregiver?.id;
    if (!cgId) return;
    const name = row.caregiver?.full_name || row.caregiver_id;
    const confirmText =
      `Permanently delete ${name} (${row.caregiver_id})?\n\n` +
      `This wipes the caregiver, all ${row.total_link_count} link record(s), ` +
      `and any unused invite tokens. Reserved for test cleanup — production ` +
      `withdrawal should use Deactivate Link instead.`;
    if (!confirm(confirmText)) return;
    setBusyAction(`${cgId}:delete`);
    try {
      await caregiverService.hardDelete(cgId);
      toast.success(`Deleted ${row.caregiver_id}.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail;
      toast.error(detail || 'Failed to delete caregiver.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleUnlock = async (row) => {
    const cgId = row.caregiver?.id;
    if (!cgId) return;
    setBusyAction(`${cgId}:unlock`);
    try {
      await caregiverService.unlock(cgId);
      toast.success('Account unlocked.');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(detail || 'Failed to unlock account.');
    } finally {
      setBusyAction(null);
    }
  };

  if (!allowCaregivers && !loading) {
    return (
      <EmptyState
        icon={Info}
        description={<>Caregiver support is not enabled for this study. Enable it in <strong className="font-medium text-gray-700 dark:text-gray-200">Edit Study &gt; Protocol Settings</strong>.</>}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Caregivers ({rows.length})
          </h3>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, ID, or email..."
            className="input pl-10 pr-3"
          />
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="btn btn-secondary h-10"
          >
            <Filter className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            Filter: {statusFilter}
          </button>
          {filterOpen && (
            <div className="absolute right-0 z-10 mt-1.5 w-52 animate-slide-down overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-float dark:border-gray-700 dark:bg-gray-800">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    setStatusFilter(opt);
                    setFilterOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                    statusFilter === opt
                      ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                      : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-white/[0.06]'
                  }`}
                >
                  <span>{opt}</span>
                  {statusFilter === opt && <Check className="h-4 w-4 flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Users} description="No caregivers have been added to this study yet." />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} description="No caregivers match your filter." />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="th-cell">
                  Name
                </th>
                <th className="th-cell">
                  Caregiver ID
                </th>
                <th className="th-cell">
                  Status
                </th>
                <th className="th-cell">
                  Linked Participants
                </th>
                <th className="th-cell">
                  Contact
                </th>
                <th className="th-cell">
                  Actions
                </th>
                <th className="th-cell w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {filtered.map((row) => {
                const cg = row.caregiver || {};
                const expandedKey = row.caregiver?.id || row.caregiver_id;
                const isOpen = expanded === expandedKey;
                const fullName =
                  cg.full_name ||
                  `${cg.first_name || ''} ${cg.last_name || ''}`.trim() ||
                  '—';
                return (
                  <React.Fragment key={expandedKey}>
                    <tr
                      className={`tr-interactive cursor-pointer ${isOpen ? 'bg-gray-50/80 dark:bg-white/[0.03]' : ''}`}
                      onClick={() => setExpanded(isOpen ? null : expandedKey)}
                    >
                      <td className="td-cell font-medium text-gray-900 dark:text-gray-100">
                        {fullName}
                      </td>
                      <td className="td-cell font-mono text-primary-700 dark:text-primary-400">
                        {row.caregiver_id}
                      </td>
                      <td className="td-cell">
                        <CaregiverStatusBadge status={row.status} />
                      </td>
                      <td className="td-cell">
                        {row.active_link_count} active
                        {row.total_link_count !== row.active_link_count && (
                          <span className="text-gray-400 dark:text-gray-500">
                            {' '}
                            / {row.total_link_count} total
                          </span>
                        )}
                      </td>
                      <td className="td-cell">
                        {cg.email || cg.phone || '—'}
                      </td>
                      <td
                        className="td-cell"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Reset PIN"
                            onClick={() => setPinResetTarget(row)}
                            className={`${ICON_BTN} text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                          {row.status === 'Locked' ? (
                            <button
                              type="button"
                              title="Unlock account"
                              disabled={busyAction === `${cg.id}:unlock`}
                              onClick={() => handleUnlock(row)}
                              className={`${ICON_BTN} text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10`}
                            >
                              <ShieldCheck className="h-4 w-4" />
                            </button>
                          ) : row.status === 'Active' ? (
                            <button
                              type="button"
                              title="Lock account"
                              disabled={busyAction === `${cg.id}:lock`}
                              onClick={() => handleLock(row)}
                              className={`${ICON_BTN} text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10`}
                            >
                              <ShieldOff className="h-4 w-4" />
                            </button>
                          ) : null}
                          {row.links?.[0]?.participant?.id && (
                            <button
                              type="button"
                              title="Open caregiver detail"
                              onClick={() =>
                                navigate(
                                  `/studies/${studyID}/sites/${row.links[0].site?.external_id}/participant/${row.links[0].participant.id}/?selected_caregiver=${cg.id}#caregivers`
                                )
                              }
                              className={`${ICON_BTN} text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-200`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                          {isSuperuser && (
                            <button
                              type="button"
                              title="Hard delete (superuser, dev cleanup)"
                              disabled={busyAction === `${cg.id}:delete`}
                              onClick={() => handleHardDelete(row)}
                              className={`${ICON_BTN} text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-500/10`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="td-cell">
                        <ChevronRight
                          className={`h-4 w-4 text-gray-400 transition-transform duration-200 dark:text-gray-500 ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/80 dark:bg-white/[0.03]">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Link history
                          </div>
                          <div className="space-y-2">
                            {row.links.map((link) => {
                              const participantLabel = link.participant?.participant_id || '—';
                              const siteLabel = link.site
                                ? `${link.site.site_name || ''} (${link.site.site_code || ''})`
                                : null;
                              return (
                                <div
                                  key={link.link_id}
                                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800"
                                >
                                  <div className="flex items-center gap-3 min-w-0">
                                    <CaregiverStatusBadge status={link.status} />
                                    <div className="text-sm min-w-0">
                                      <div className="text-gray-900 dark:text-gray-100">
                                        Participant {participantLabel} · {link.relationship_type}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {siteLabel}
                                        {link.deactivated_at && (
                                          <>
                                            {' '}· Deactivated{' '}
                                            {new Date(link.deactivated_at).toLocaleDateString()}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    {link.is_active && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeactivateTarget({
                                            link_id: link.link_id,
                                            caregiver: row.caregiver,
                                            relationship_type: link.relationship_type,
                                            caregiver_id: row.caregiver_id,
                                          });
                                        }}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-danger-600 underline-offset-4 hover:underline dark:text-danger-400"
                                      >
                                        <Unlink className="h-3.5 w-3.5" />
                                        Deactivate
                                      </button>
                                    )}
                                    {link.participant?.id && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(
                                            `/studies/${studyID}/sites/${link.site?.external_id}/participant/${link.participant.id}/?selected_caregiver=${row.caregiver?.id || ''}#caregivers`
                                          );
                                        }}
                                        className="link text-xs"
                                      >
                                        Open
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pinResetTarget && (
        <ResetPinModal
          caregiverId={pinResetTarget.caregiver?.id}
          caregiverLabel={`${pinResetTarget.caregiver?.full_name || ''} (${pinResetTarget.caregiver_id})`}
          onClose={() => setPinResetTarget(null)}
          onResetComplete={() => {
            setPinResetTarget(null);
            load();
          }}
        />
      )}

      {deactivateTarget && (
        <DeactivateLinkConfirm
          link={deactivateTarget}
          caregiverId={deactivateTarget.caregiver?.id}
          onClose={() => setDeactivateTarget(null)}
          onConfirmed={() => {
            setDeactivateTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default StudyCaregiversTab;
